import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { MailFolder } from '@microsoft/microsoft-graph-types'
import BaseFolderCombobox, { BaseFolderComboboxProps, getRootFolders } from './BaseFolderCombobox'

type Props = Omit<BaseFolderComboboxProps<ChildFolderOption>, 'entities' | 'getEntityLabel' | 'getEntityValue'>
type ChildFolderOption = Pick<MailFolder, 'id' | 'parentFolderId' | 'displayName' | 'childFolders' | 'childFolderCount'>

export default function ChildFolderCombobox(props: Props): ReactElement {
    const options = useMemo<ChildFolderOption[]>(() => {
        if (props.rootFolderId.loaded && props.folders.loaded) {
            const folderOptions = [...props.folders.data.values()].reduce(
                (map: Map<string, ChildFolderOption>, folder: MailFolder) => {
                    folder.childFolders = undefined
                    folder.childFolderCount = undefined
                    return map.set(folder.id, folder)
                },
                new Map<string, ChildFolderOption>(),
            )

            folderOptions.forEach(option => {
                const parentFolderId = option.parentFolderId
                if (folderOptions.has(parentFolderId)) {
                    // Use the object reference to set all children.
                    const parentFolder = folderOptions.get(parentFolderId)
                    parentFolder.childFolders = [...(parentFolder.childFolders || []), option]
                    parentFolder.childFolderCount = parentFolder.childFolders?.length
                }
            })

            return (
                getRootFolders(props)
                    .map(folder => folderOptions.get(folder.id))
                    // Filter out all empty root folders.
                    .filter(folder => folder.childFolderCount > 0)
            )
        }
        return []
    }, [props.rootFolderId.data, props.folders.data])

    return (
        <BaseFolderCombobox<ChildFolderOption>
            entities={options}
            isGroup={(entity: ChildFolderOption) => entity.childFolderCount > 0}
            getGroupLabel={(entity: ChildFolderOption) => entity.displayName}
            getGroupValue={(entity: ChildFolderOption) => entity.id}
            getGroupEntities={(entity: ChildFolderOption) => entity.childFolders}
            getEntityLabel={(entity: ChildFolderOption) => entity.displayName}
            getEntityValue={(entity: ChildFolderOption) => entity.id}
            {...props}
        />
    )
}
